/**
 * Hook for fetching parent assets via graph traversal
 *
 * Queries for assets that discovered the current asset through DISCOVERED,
 * or HAS_PORT relationships (2 hops away in the graph).
 */

import { useGraphQuery } from '@/hooks/useGraphQuery';
import { TreeNode } from '@/types';

export interface UseParentAssetsQueryResult {
  /** Parent assets tree structure from graph query */
  data: TreeNode[] | undefined;
  /** True if query is in initial loading state */
  isLoading: boolean;
  /** Query status */
  status: 'pending' | 'error' | 'success';
}

/**
 * Custom hook for fetching parent assets that discovered the current asset
 *
 * Traverses the graph to find assets connected through:
 * - DISCOVERED relationship (direct discovery)
 * - HAS_PORT relationship (port-mediated discovery)
 *
 * Uses 2-hop relationship traversal to find immediate parents.
 *
 * @param assetKey - The asset key to find parents for
 * @returns Parent assets tree data with loading state
 */
export function useParentAssetsQuery(
  assetKey: string
): UseParentAssetsQueryResult {
  const { data, isLoading, status } = useGraphQuery({
    tree: true,
    query: {
      node: {
        labels: ['Asset'],
        self_edges: 'forbid',
        relationships: [
          {
            label: ['DISCOVERED', 'HAS_PORT'],
            length: 2,
            target: {
              labels: ['Asset'],
              filters: [
                {
                  field: 'key',
                  operator: '=',
                  value: assetKey,
                },
              ],
            },
          },
        ],
      },
    },
  });

  return {
    data,
    isLoading,
    status,
  };
}
